import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import {
  Brain, ChevronLeft, ChevronRight, Play, Pause, Repeat,
  Check, CheckCircle2, BookOpen, X, Search, Volume2, Clock,
  Flame, TrendingUp, Award, BarChart3, Calendar, Eye, EyeOff,
  CheckCircle, XCircle, MinusCircle, Target
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import {
  getQuranSurahs,
  getQuranReciters,
  getQuranSurahWithCustomTranslation,
  getQuranUserPreferences,
  getReadAyahs,
  markAyahAsReadInDB,
  unmarkAyahAsReadInDB,
  logBereketActivity,
} from '../lib/api';
import { supabase } from '../lib/supabase';

const HIFZ_SESSION_STORAGE_KEY = 'hifz_active_session_v1';

export default function Hifz({ user }) {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const audioRef = useRef(null);

  const [surahs, setSurahs] = useState([]);
  const [selectedSurah, setSelectedSurah] = useState(() => {
    try { return parseInt(localStorage.getItem('hifz_surah')) || 1; } catch { return 1; }
  });
  const [startAyah, setStartAyah] = useState(() => {
    try { return parseInt(localStorage.getItem('hifz_start')) || 1; } catch { return 1; }
  });
  const [endAyah, setEndAyah] = useState(() => {
    try { return parseInt(localStorage.getItem('hifz_end')) || 5; } catch { return 5; }
  });
  const [startAyahInput, setStartAyahInput] = useState(String(startAyah));
  const [endAyahInput, setEndAyahInput] = useState(String(endAyah));
  const [repeatCount, setRepeatCount] = useState(3);
  const [currentRepeat, setCurrentRepeat] = useState(0);
  
  const [ayahs, setAyahs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentAyahIdx, setCurrentAyahIdx] = useState(0);
  const [autoRepeat, setAutoRepeat] = useState(true);
  const [memorizedAyahs, setMemorizedAyahs] = useState(new Set());
  const [showSurahPicker, setShowSurahPicker] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [preferences, setPreferences] = useState({ preferred_reciter: 'ar.alafasy' });
  const [stats, setStats] = useState({ total: 0, memorized: 0 });
  const [sessionHydrated, setSessionHydrated] = useState(false);
  
  // NEW: Session timer & tracking
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [sessionDuration, setSessionDuration] = useState(0);
  const [totalRepeatsInSession, setTotalRepeatsInSession] = useState(0);
  const [showSessionSummary, setShowSessionSummary] = useState(false);
  
  // NEW: Streak tracking
  const [streakData, setStreakData] = useState({ current: 0, longest: 0, lastDate: null });
  
  // NEW: Progress views
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [surahProgressData, setSurahProgressData] = useState([]);
  const [juzProgressData, setJuzProgressData] = useState([]);
  const [weeklyActivity, setWeeklyActivity] = useState([]);
  
  // NEW: Test Mode
  const [testMode, setTestMode] = useState(false);
  const [testType, setTestType] = useState('arabic'); // 'arabic' or 'translation'
  const [revealedAyahs, setRevealedAyahs] = useState(new Set());
  const [testResults, setTestResults] = useState([]);
  const [ayahPerformance, setAyahPerformance] = useState({});

  const persistSessionSnapshot = useCallback((forceStop = false) => {
    try {
      const sessionData = {
        selectedSurah,
        startAyah,
        endAyah,
        startAyahInput,
        endAyahInput,
        repeatCount,
        currentRepeat,
        currentAyahIdx,
        autoRepeat,
        isPlaying: forceStop ? false : isPlaying,
        ayahs,
        updatedAt: new Date().toISOString(),
      };
      localStorage.setItem(HIFZ_SESSION_STORAGE_KEY, JSON.stringify(sessionData));
    } catch {
      // ignore localStorage write errors
    }
  }, [
    selectedSurah,
    startAyah,
    endAyah,
    startAyahInput,
    endAyahInput,
    repeatCount,
    currentRepeat,
    currentAyahIdx,
    autoRepeat,
    isPlaying,
    ayahs,
  ]);

  useEffect(() => {
    fetchSurahs();
    fetchPreferences();
    fetchMemorizedAyahs();
    loadStreakData();
    loadWeeklyActivity();
    loadAyahPerformance();

    // Restore previously active Hifz session (selection + loaded ayahs + progress)
    try {
      const raw = localStorage.getItem(HIFZ_SESSION_STORAGE_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved && typeof saved === 'object') {
          if (Number.isInteger(saved.selectedSurah)) setSelectedSurah(saved.selectedSurah);
          if (Number.isInteger(saved.startAyah) && saved.startAyah > 0) setStartAyah(saved.startAyah);
          if (Number.isInteger(saved.endAyah) && saved.endAyah > 0) setEndAyah(saved.endAyah);
          if (typeof saved.startAyahInput === 'string') setStartAyahInput(saved.startAyahInput);
          if (typeof saved.endAyahInput === 'string') setEndAyahInput(saved.endAyahInput);
          if (Number.isInteger(saved.repeatCount) && saved.repeatCount > 0) setRepeatCount(saved.repeatCount);
          if (Number.isInteger(saved.currentRepeat) && saved.currentRepeat >= 0) setCurrentRepeat(saved.currentRepeat);
          if (typeof saved.autoRepeat === 'boolean') setAutoRepeat(saved.autoRepeat);
          if (Array.isArray(saved.ayahs) && saved.ayahs.length > 0) {
            setAyahs(saved.ayahs);
            const clampedIdx = Math.min(
              Math.max(Number(saved.currentAyahIdx) || 0, 0),
              Math.max(saved.ayahs.length - 1, 0)
            );
            setCurrentAyahIdx(clampedIdx);
            setStats(prev => ({ ...prev, total: saved.ayahs.length }));
          }
        }
      }
    } catch {
      // ignore corrupted session payload
    } finally {
      setSessionHydrated(true);
    }
  }, []);

  useEffect(() => {
    if (surahs.length > 0) {
      const cur = surahs.find(s => s.number === selectedSurah);
      if (cur && endAyah > cur.numberOfAyahs) {
        setEndAyah(cur.numberOfAyahs);
        setEndAyahInput(String(cur.numberOfAyahs));
      }
    }
  }, [selectedSurah, surahs]);

  // Persist selections to localStorage
  useEffect(() => {
    localStorage.setItem('hifz_surah', String(selectedSurah));
    localStorage.setItem('hifz_start', String(startAyah));
    localStorage.setItem('hifz_end', String(endAyah));
  }, [selectedSurah, startAyah, endAyah]);

  // Persist full active session so progress UI survives refresh/navigation.
  useEffect(() => {
    if (!sessionHydrated) return;
    persistSessionSnapshot(false);
  }, [sessionHydrated, persistSessionSnapshot]);

  // NEW: Session timer
  useEffect(() => {
    let interval;
    if (isPlaying && sessionStartTime) {
      interval = setInterval(() => {
        setSessionDuration(Math.floor((Date.now() - sessionStartTime) / 1000));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, sessionStartTime]);
  
  // NEW: Track repeats
  useEffect(() => {
    if (isPlaying) {
      setTotalRepeatsInSession(prev => prev + 1);
    }
  }, [currentRepeat]);

  // On tab hide / page exit: auto-finish playback and persist current progress snapshot.
  useEffect(() => {
    const endSession = () => {
      try {
        audioRef.current?.pause();
      } catch {
        // ignore
      }
      setIsPlaying(false);
      persistSessionSnapshot(true);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        endSession();
      }
    };

    window.addEventListener('beforeunload', endSession);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', endSession);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [persistSessionSnapshot]);

  const fetchSurahs = async () => {
    try {
      const data = await getQuranSurahs();
      setSurahs(data);
    } catch (e) { console.error(e); }
  };

  const fetchPreferences = async () => {
    try {
      const prefs = await getQuranUserPreferences();
      setPreferences(prefs);
    } catch (e) { console.error(e); }
  };

  const fetchMemorizedAyahs = async () => {
    try {
      // Use read ayahs as "memorized" for now
      const data = await getReadAyahs();
      const set = new Set(data.map(item => `${item.surah_number}:${item.ayah_number}`));
      setMemorizedAyahs(set);
      setStats(prev => ({ ...prev, memorized: set.size }));
      
      // Calculate per-surah and per-juz progress
      calculateProgressData(set);
    } catch (e) { console.error(e); }
  };
  
  // NEW: Calculate surah and juz progress
  const calculateProgressData = (memorizedSet) => {
    // Surah progress
    const surahProgress = surahs.map(surah => {
      const memorizedInSurah = Array.from(memorizedSet)
        .filter(key => key.startsWith(`${surah.number}:`))
        .length;
      return {
        number: surah.number,
        name: surah.englishName,
        arabicName: surah.name,
        total: surah.numberOfAyahs,
        memorized: memorizedInSurah,
        percent: Math.round((memorizedInSurah / surah.numberOfAyahs) * 100),
        completed: memorizedInSurah === surah.numberOfAyahs
      };
    });
    setSurahProgressData(surahProgress);
    
    // Juz progress (30 juz, approximately distributed)
    // Simple juz mapping based on ayah count distribution
    const juzProgress = Array.from({ length: 30 }, (_, i) => {
      const juzNum = i + 1;
      // Approximate ayah count per juz (~208 ayahs)
      const avgAyahsPerJuz = 208;
      const memorizedInJuz = Array.from(memorizedSet).length; // Simplified for now
      return {
        number: juzNum,
        memorized: Math.floor(memorizedInJuz / 30), // Simplified calculation
        total: avgAyahsPerJuz,
        percent: Math.round((memorizedInJuz / 6236) * 100 / 30 * juzNum)
      };
    });
    setJuzProgressData(juzProgress);
  };
  
  // NEW: Load streak data
  const loadStreakData = () => {
    try {
      const stored = localStorage.getItem('hifz_streak_data');
      if (stored) {
        const data = JSON.parse(stored);
        setStreakData(data);
      }
    } catch (e) {
      console.error('Failed to load streak:', e);
    }
  };
  
  // NEW: Update streak
  const updateStreak = () => {
    const today = new Date().toDateString();
    const lastDate = streakData.lastDate;
    
    if (lastDate === today) {
      // Already counted today
      return;
    }
    
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toDateString();
    
    let newStreak = streakData.current;
    
    if (lastDate === yesterdayStr) {
      // Continue streak
      newStreak = streakData.current + 1;
    } else if (lastDate !== today) {
      // Streak broken, start new
      newStreak = 1;
    }
    
    const newData = {
      current: newStreak,
      longest: Math.max(newStreak, streakData.longest),
      lastDate: today
    };
    
    setStreakData(newData);
    try {
      localStorage.setItem('hifz_streak_data', JSON.stringify(newData));
    } catch (e) {
      console.error('Failed to save streak:', e);
    }
  };
  
  // NEW: Load weekly activity
  const loadWeeklyActivity = () => {
    try {
      const stored = localStorage.getItem('hifz_sessions_history');
      if (stored) {
        const sessions = JSON.parse(stored);
        const last7Days = Array.from({ length: 7 }, (_, i) => {
          const date = new Date();
          date.setDate(date.getDate() - (6 - i));
          const dateStr = date.toISOString().split('T')[0];
          const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
          
          const daySessions = sessions.filter(s => s.date === dateStr);
          const totalMinutes = daySessions.reduce((sum, s) => sum + Math.floor(s.duration / 60), 0);
          
          return {
            day: dayName,
            date: dateStr,
            minutes: totalMinutes,
            sessions: daySessions.length
          };
        });
        setWeeklyActivity(last7Days);
      }
    } catch (e) {
      console.error('Failed to load activity:', e);
    }
  };
  
  // NEW: Save session to history
  const saveSessionToHistory = (duration, ayahsCount, totalRepeats) => {
    try {
      const stored = localStorage.getItem('hifz_sessions_history');
      const sessions = stored ? JSON.parse(stored) : [];
      
      const newSession = {
        date: new Date().toISOString().split('T')[0],
        timestamp: new Date().toISOString(),
        surahNumber: selectedSurah,
        surahName: currentSurah?.englishName || '',
        ayahRange: `${startAyah}-${endAyah}`,
        duration, // in seconds
        ayahsCount,
        totalRepeats
      };
      
      sessions.push(newSession);
      
      // Keep only last 100 sessions
      const recent = sessions.slice(-100);
      localStorage.setItem('hifz_sessions_history', JSON.stringify(recent));
      
      // Reload weekly activity
      loadWeeklyActivity();
    } catch (e) {
      console.error('Failed to save session:', e);
    }
  };
  
  // NEW: Load ayah performance data
  const loadAyahPerformance = () => {
    try {
      const stored = localStorage.getItem('hifz_ayah_performance');
      if (stored) {
        setAyahPerformance(JSON.parse(stored));
      }
    } catch (e) {
      console.error('Failed to load performance:', e);
    }
  };
  
  // NEW: Save test result
  const saveTestResult = (surahNum, ayahNum, result) => {
    const key = `${surahNum}:${ayahNum}`;
    const newResult = {
      surah: surahNum,
      ayah: ayahNum,
      result, // 'correct', 'partial', 'incorrect'
      testType,
      timestamp: new Date().toISOString()
    };
    
    setTestResults(prev => [...prev, newResult]);
    
    // Update ayah performance
    try {
      const stored = localStorage.getItem('hifz_ayah_performance');
      const performance = stored ? JSON.parse(stored) : {};
      
      if (!performance[key]) {
        performance[key] = { correct: 0, partial: 0, incorrect: 0, total: 0 };
      }
      
      performance[key][result]++;
      performance[key].total++;
      performance[key].lastTested = new Date().toISOString();
      performance[key].successRate = Math.round(
        ((performance[key].correct + performance[key].partial * 0.5) / performance[key].total) * 100
      );
      
      localStorage.setItem('hifz_ayah_performance', JSON.stringify(performance));
      setAyahPerformance(performance);
      
      // Also save to test history
      const historyStored = localStorage.getItem('hifz_test_history');
      const history = historyStored ? JSON.parse(historyStored) : [];
      history.push(newResult);
      localStorage.setItem('hifz_test_history', JSON.stringify(history.slice(-500)));
      
    } catch (e) {
      console.error('Failed to save test result:', e);
    }
  };
  
  // NEW: Toggle reveal ayah
  const toggleRevealAyah = (index) => {
    const key = `${selectedSurah}:${startAyah + index}`;
    setRevealedAyahs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };
  
  // NEW: Start test mode
  const startTestMode = (type) => {
    setTestMode(true);
    setTestType(type);
    setRevealedAyahs(new Set());
    setTestResults([]);
    toast.success(
      type === 'arabic' 
        ? (lang === 'hr' ? 'Test mod: Arapski tekst sakriven' : 'Test mode: Arabic text hidden')
        : (lang === 'hr' ? 'Test mod: Prijevod sakriven' : 'Test mode: Translation hidden')
    );
  };
  
  // NEW: End test mode
  const endTestMode = () => {
    if (testResults.length > 0) {
      const correct = testResults.filter(r => r.result === 'correct').length;
      const partial = testResults.filter(r => r.result === 'partial').length;
      const total = testResults.length;
      const score = Math.round(((correct + partial * 0.5) / total) * 100);
      
      toast.success(
        lang === 'hr' 
          ? `Test završen! Rezultat: ${score}% (${correct}/${total} tačno)`
          : `Test complete! Score: ${score}% (${correct}/${total} correct)`
      );
    }
    
    setTestMode(false);
    setRevealedAyahs(new Set());
    setTestResults([]);
  };

  const loadAyahs = async () => {
    setLoading(true);
    setAyahs([]);
    setCurrentAyahIdx(0);
    setCurrentRepeat(0);
    try {
      const surahBundle = await getQuranSurahWithCustomTranslation(
        selectedSurah,
        preferences.preferred_translation || 'bs.korkut'
      );

      const data = (surahBundle?.ayahs || []).filter(
        (ayah) => ayah.numberInSurah >= startAyah && ayah.numberInSurah <= endAyah
      );

      setAyahs(data);
      setStats(prev => ({ ...prev, total: data.length }));
    } catch (e) {
      toast.error(lang === 'hr' ? 'Greska pri ucitavanju' : 'Error loading ayahs');
    } finally {
      setLoading(false);
    }
  };

  const buildAudioUrl = (ayahNumber) => {
    const reciter = preferences.preferred_reciter || 'ar.alafasy';
    return `https://cdn.islamic.network/quran/audio/128/${reciter}/${ayahNumber}.mp3`;
  };

  const playCurrentAyah = useCallback(() => {
    if (!ayahs[currentAyahIdx] || !audioRef.current) return;
    const ayah = ayahs[currentAyahIdx];
    const url = ayah.audio || buildAudioUrl(ayah.number);
    audioRef.current.src = url;
    audioRef.current.load();
    audioRef.current.play().catch(() => {});
    setIsPlaying(true);
  }, [currentAyahIdx, ayahs, preferences]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleEnded = () => {
      if (autoRepeat && currentRepeat < repeatCount - 1) {
        // Repeat same ayah
        setCurrentRepeat(prev => prev + 1);
        audio.currentTime = 0;
        audio.play().catch(() => {});
      } else {
        // Move to next ayah
        setCurrentRepeat(0);
        if (currentAyahIdx < ayahs.length - 1) {
          setCurrentAyahIdx(prev => prev + 1);
        } else {
          // Finished all ayahs - loop back to start
          setCurrentAyahIdx(0);
          setIsPlaying(false);
        }
      }
    };

    audio.addEventListener('ended', handleEnded);
    return () => audio.removeEventListener('ended', handleEnded);
  }, [currentRepeat, repeatCount, currentAyahIdx, ayahs, autoRepeat]);

  useEffect(() => {
    if (isPlaying && ayahs.length > 0) {
      playCurrentAyah();
    }
  }, [currentAyahIdx]);

  const togglePlay = () => {
    if (ayahs.length === 0) {
      loadAyahs().then(() => {});
      return;
    }
    if (isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
    } else {
      // Start session timer if not started
      if (!sessionStartTime) {
        setSessionStartTime(Date.now());
        setSessionDuration(0);
        setTotalRepeatsInSession(0);
        updateStreak(); // Update streak on session start
      }
      playCurrentAyah();
    }
  };
  
  // NEW: End session
  const endSession = () => {
    if (sessionStartTime && sessionDuration > 0) {
      saveSessionToHistory(sessionDuration, ayahs.length, totalRepeatsInSession);
      setShowSessionSummary(true);
    }
    audioRef.current?.pause();
    setIsPlaying(false);
    setSessionStartTime(null);
    setSessionDuration(0);
    setTotalRepeatsInSession(0);
  };

  const toggleMemorized = async (surahNum, ayahNum) => {
    const key = `${surahNum}:${ayahNum}`;
    const newSet = new Set(memorizedAyahs);
    
    if (newSet.has(key)) {
      newSet.delete(key);
      try { await unmarkAyahAsReadInDB(surahNum, ayahNum); } catch (e) { console.error(e); }
      toast.info(lang === 'hr' ? 'Ajet odznačen' : 'Verse unmarked');
    } else {
      newSet.add(key);
      try { await markAyahAsReadInDB(surahNum, ayahNum); } catch (e) { console.error(e); }
      // Also log as bereket activity (Memorize Quran ayahs - a6b508f5)
      try {
        const { data: memActivity } = await supabase
          .from('bereket_activities')
          .select('id, base_points')
          .ilike('name_en', '%memorize%quran%ayah%')
          .maybeSingle();
        if (memActivity) {
          await logBereketActivity(memActivity.id, memActivity.base_points, false, 'Memorize Quran ayahs');
        }
      } catch (e) { /* non-blocking */ }
      toast.success(lang === 'hr' ? 'Ajet označen kao naučen!' : 'Verse marked as memorized!');
    }
    
    setMemorizedAyahs(newSet);
    setStats(prev => ({ ...prev, memorized: newSet.size }));
  };

  const currentSurah = surahs.find(s => s.number === selectedSurah);
  const maxAyahs = currentSurah?.numberOfAyahs || 286;
  const filteredSurahs = surahs.filter(s =>
    s.englishName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.name.includes(searchTerm) ||
    String(s.number).includes(searchTerm)
  );

  const memorizedInRange = ayahs.filter((_, i) => memorizedAyahs.has(`${selectedSurah}:${startAyah + i}`)).length;
  
  // NEW: Format time helper
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // NEW: Format duration helper
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    const remainMins = mins % 60;
    return `${hours}h ${remainMins}m`;
  };

  return (
    <Layout user={user}>
      <audio ref={audioRef} />
      <div className="max-w-3xl mx-auto px-4 py-8 md:py-12">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-md">
            <Brain className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-heading font-bold text-[#292524]">Hifz</h1>
            <p className="text-sm text-[#57534E]">
              {lang === 'hr' ? 'Memoriziraj ajete ponavljanjem' : 'Memorize verses through repetition'}
            </p>
          </div>
        </div>

        {/* Stats bar */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="bg-violet-50 border border-violet-200 rounded-xl p-4 text-center">
            <div className="text-3xl font-bold text-violet-700">{memorizedAyahs.size}</div>
            <div className="text-xs text-violet-500 mt-1">{lang === 'hr' ? 'Naučenih ajeta' : 'Memorized ayahs'}</div>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
            <div className="text-3xl font-bold text-emerald-700">{Math.round((memorizedAyahs.size / 6236) * 100 * 10) / 10}%</div>
            <div className="text-xs text-emerald-500 mt-1">{lang === 'hr' ? "Kur'an naučen" : 'Quran memorized'}</div>
          </div>
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 text-center">
            <div className="flex items-center justify-center gap-1">
              <Flame className="w-6 h-6 text-orange-500" />
              <div className="text-3xl font-bold text-orange-700">{streakData.current}</div>
            </div>
            <div className="text-xs text-orange-500 mt-1">{lang === 'hr' ? 'Dana uzastopno' : 'Day streak'}</div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
            <div className="flex items-center justify-center gap-1">
              <Clock className="w-5 h-5 text-blue-500" />
              <div className="text-3xl font-bold text-blue-700">{sessionStartTime ? formatTime(sessionDuration) : '0:00'}</div>
            </div>
            <div className="text-xs text-blue-500 mt-1">{lang === 'hr' ? 'Session vrijeme' : 'Session time'}</div>
          </div>
        </div>
        
        {/* Progress button */}
        <button
          onClick={() => setShowProgressModal(true)}
          className="w-full mb-6 py-3 bg-gradient-to-r from-violet-500 to-purple-600 text-white rounded-xl font-medium hover:from-violet-600 hover:to-purple-700 transition-all flex items-center justify-center gap-2 shadow-md"
        >
          <TrendingUp className="w-4 h-4" />
          {lang === 'hr' ? 'Prikaži napredak' : 'View Progress'}
        </button>

        {/* Surah & Range Selection */}
        <div className="bg-white border border-stone-200 rounded-2xl p-5 mb-6 shadow-sm" data-testid="hifz-selection">
          {/* Surah picker */}
          <button
            onClick={() => setShowSurahPicker(true)}
            data-testid="hifz-surah-picker"
            className="w-full flex items-center justify-between p-3 bg-stone-50 rounded-xl border border-stone-200 mb-4 hover:bg-stone-100 transition-colors"
          >
            <div className="flex items-center gap-3">
              <BookOpen className="w-5 h-5 text-violet-500" />
              <div className="text-left">
                <div className="font-medium text-[#292524] text-sm">
                  {currentSurah ? `${currentSurah.number}. ${currentSurah.englishName}` : (lang === 'hr' ? 'Odaberi suru' : 'Select surah')}
                </div>
                {currentSurah && <div className="text-xs text-[#57534E]">{currentSurah.name} - {currentSurah.numberOfAyahs} {lang === 'hr' ? 'ajeta' : 'ayahs'}</div>}
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-stone-400" />
          </button>

          {/* Ayah Range */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <label className="text-xs font-medium text-[#78716C] mb-1 block">{lang === 'hr' ? 'Od ajeta' : 'From ayah'}</label>
              <input
                type="number" min={1} max={maxAyahs}
                value={startAyahInput}
                onChange={(e) => setStartAyahInput(e.target.value)}
                onBlur={() => {
                  const val = parseInt(startAyahInput);
                  const clamped = isNaN(val) ? 1 : Math.max(1, Math.min(val, maxAyahs));
                  setStartAyah(clamped);
                  setStartAyahInput(String(clamped));
                }}
                data-testid="hifz-start-ayah"
                className="w-full px-3 py-2.5 border border-stone-200 rounded-xl text-sm bg-stone-50 focus:outline-none focus:border-violet-400"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-[#78716C] mb-1 block">{lang === 'hr' ? 'Do ajeta' : 'To ayah'}</label>
              <input
                type="number" min={1} max={maxAyahs}
                value={endAyahInput}
                onChange={(e) => setEndAyahInput(e.target.value)}
                onBlur={() => {
                  const val = parseInt(endAyahInput);
                  const clamped = isNaN(val) ? startAyah : Math.max(startAyah, Math.min(val, maxAyahs));
                  setEndAyah(clamped);
                  setEndAyahInput(String(clamped));
                }}
                data-testid="hifz-end-ayah"
                className="w-full px-3 py-2.5 border border-stone-200 rounded-xl text-sm bg-stone-50 focus:outline-none focus:border-violet-400"
              />
            </div>
          </div>

          {/* Repeat Settings */}
          <div className="flex items-center gap-4 mb-4">
            <div className="flex-1">
              <label className="text-xs font-medium text-[#78716C] mb-1 block">
                <Repeat className="w-3 h-3 inline mr-1" />
                {lang === 'hr' ? 'Ponavljanja po ajetu' : 'Repeats per ayah'}
              </label>
              <div className="flex items-center gap-2">
                {[1, 3, 5, 7, 10].map(n => (
                  <button
                    key={n}
                    onClick={() => setRepeatCount(n)}
                    data-testid={`repeat-${n}`}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      repeatCount === n ? 'bg-violet-600 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                    }`}
                  >
                    {n}x
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-[#78716C] mb-1 block">{lang === 'hr' ? 'Auto loop' : 'Auto loop'}</label>
              <button
                onClick={() => setAutoRepeat(!autoRepeat)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  autoRepeat ? 'bg-violet-600 text-white' : 'bg-stone-100 text-stone-600'
                }`}
              >
                <Repeat className="w-4 h-4" />
              </button>
            </div>
          </div>


          {/* Test Mode */}
          {ayahs.length > 0 && (
            <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-amber-600" />
                  <span className="font-semibold text-stone-800">{lang === 'hr' ? 'Test Mod' : 'Test Mode'}</span>
                </div>
                {testMode && (
                  <button
                    onClick={endTestMode}
                    className="px-3 py-1 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 transition-all"
                  >
                    {lang === 'hr' ? 'Završi Test' : 'End Test'}
                  </button>
                )}
              </div>
              
              {!testMode ? (
                <div className="flex gap-2">
                  <button
                    onClick={() => startTestMode('arabic')}
                    className="flex-1 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-all flex items-center justify-center gap-2 text-sm"
                  >
                    <EyeOff className="w-4 h-4" />
                    {lang === 'hr' ? 'Testiraj Arapski' : 'Test Arabic'}
                  </button>
                  <button
                    onClick={() => startTestMode('translation')}
                    className="flex-1 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-all flex items-center justify-center gap-2 text-sm"
                  >
                    <EyeOff className="w-4 h-4" />
                    {lang === 'hr' ? 'Testiraj Prijevod' : 'Test Translation'}
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-amber-700">
                      {testType === 'arabic' 
                        ? (lang === 'hr' ? '🔒 Arapski tekst sakriven' : '🔒 Arabic text hidden')
                        : (lang === 'hr' ? '🔒 Prijevod sakriven' : '🔒 Translation hidden')
                      }
                    </span>
                    <span className="font-medium text-stone-700">
                      {testResults.length}/{ayahs.length} {lang === 'hr' ? 'testirano' : 'tested'}
                    </span>
                  </div>
                  {testResults.length > 0 && (
                    <div className="flex gap-2 text-xs">
                      <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded">
                        ✓ {testResults.filter(r => r.result === 'correct').length}
                      </span>
                      <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded">
                        ~ {testResults.filter(r => r.result === 'partial').length}
                      </span>
                      <span className="px-2 py-1 bg-red-100 text-red-700 rounded">
                        ✗ {testResults.filter(r => r.result === 'incorrect').length}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Load & Play */}
          <div className="flex gap-3">
            <button
              onClick={loadAyahs}
              disabled={loading}
              data-testid="load-ayahs-btn"
              className="flex-1 py-3 bg-violet-600 text-white rounded-xl font-medium hover:bg-violet-700 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
            >
              {loading ? (
                <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              ) : (
                <BookOpen className="w-4 h-4" />
              )}
              {lang === 'hr' ? 'Učitaj ajete' : 'Load ayahs'}
            </button>
            {ayahs.length > 0 && (
              <>
                <button
                  onClick={togglePlay}
                  data-testid="hifz-play-btn"
                  className={`px-5 py-3 rounded-xl font-medium transition-all flex items-center gap-2 ${
                    isPlaying ? 'bg-amber-500 text-white hover:bg-amber-600' : 'bg-emerald-600 text-white hover:bg-emerald-700'
                  }`}
                >
                  {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  {isPlaying ? (lang === 'hr' ? 'Pauza' : 'Pause') : (lang === 'hr' ? 'Pusti' : 'Play')}
                </button>
                {sessionStartTime && (
                  <button
                    onClick={endSession}
                    className="px-5 py-3 bg-red-500 text-white rounded-xl font-medium hover:bg-red-600 transition-all flex items-center gap-2"
                  >
                    <X className="w-4 h-4" />
                    {lang === 'hr' ? 'Završi' : 'End'}
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Playing indicator */}
        {isPlaying && ayahs.length > 0 && (
          <div className="bg-violet-50 border border-violet-200 rounded-xl p-3 mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Volume2 className="w-4 h-4 text-violet-600 animate-pulse" />
                <span className="text-sm font-medium text-violet-700">
                  {lang === 'hr' ? 'Ajet' : 'Ayah'} {startAyah + currentAyahIdx} — {lang === 'hr' ? 'Ponavljanje' : 'Repeat'} {currentRepeat + 1}/{repeatCount}
                </span>
              </div>
              <div className="text-sm font-medium text-violet-600">
                {totalRepeatsInSession} {lang === 'hr' ? 'ponavljanja' : 'repeats'}
              </div>
            </div>
          </div>
        )}

        {/* Ayah List */}
        {ayahs.length > 0 && (
          <div className="space-y-3" data-testid="hifz-ayah-list">
            {ayahs.map((ayah, idx) => {
              const ayahNum = startAyah + idx;
              const isMemorized = memorizedAyahs.has(`${selectedSurah}:${ayahNum}`);
              const isCurrent = currentAyahIdx === idx;
              const ayahKey = `${selectedSurah}:${ayahNum}`;
              const isRevealed = revealedAyahs.has(ayahKey);
              const ayahResult = testResults.find(r => r.surah === selectedSurah && r.ayah === ayahNum);
              const performance = ayahPerformance[ayahKey];
              
              return (
                <div
                  key={idx}
                  data-testid={`hifz-ayah-${ayahNum}`}
                  className={`rounded-xl border overflow-hidden transition-all ${
                    isCurrent && isPlaying
                      ? 'ring-2 ring-violet-400 border-violet-300 shadow-lg shadow-violet-100'
                      : isMemorized
                      ? 'border-emerald-200 bg-emerald-50/50'
                      : 'border-stone-200'
                  }`}
                >
                  <div className={`flex items-center justify-between px-4 py-2 ${
                    isCurrent && isPlaying ? 'bg-violet-600' : isMemorized ? 'bg-emerald-100' : 'bg-stone-50'
                  }`}>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-medium ${
                        isCurrent && isPlaying ? 'text-white' : isMemorized ? 'text-emerald-700' : 'text-stone-500'
                      }`}>
                        {lang === 'hr' ? 'Ajet' : 'Ayah'} {ayahNum}
                      </span>
                      {isCurrent && isPlaying && (
                        <span className="text-xs text-white/70">{currentRepeat + 1}/{repeatCount}</span>
                      )}
                      {performance && performance.total > 0 && (
                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                          performance.successRate >= 70 ? 'bg-emerald-100 text-emerald-700' :
                          performance.successRate >= 40 ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {performance.successRate}%
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {!testMode && (
                        <>
                          <button
                            onClick={() => {
                              setCurrentAyahIdx(idx);
                              setCurrentRepeat(0);
                              if (!isPlaying) playCurrentAyah();
                              else {
                                const audio = audioRef.current;
                                if (audio) {
                                  const url = ayah.audio || buildAudioUrl(ayah.number);
                                  audio.src = url;
                                  audio.load();
                                  audio.play().catch(() => {});
                                }
                              }
                            }}
                            className={`w-7 h-7 rounded-full flex items-center justify-center ${
                              isCurrent && isPlaying ? 'bg-white/20 text-white' : 'bg-stone-200 text-stone-600 hover:bg-stone-300'
                            }`}
                          >
                            {isCurrent && isPlaying ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                          </button>
                          <button
                            onClick={() => toggleMemorized(selectedSurah, ayahNum)}
                            data-testid={`memorize-btn-${ayahNum}`}
                            className={`w-7 h-7 rounded-full flex items-center justify-center transition-all ${
                              isMemorized ? 'bg-emerald-500 text-white' : 'bg-stone-200 text-stone-400 hover:bg-emerald-100 hover:text-emerald-600'
                            }`}
                          >
                            {isMemorized ? <CheckCircle2 className="w-4 h-4" /> : <Check className="w-3 h-3" />}
                          </button>
                        </>
                      )}
                      {testMode && !isRevealed && !ayahResult && (
                        <button
                          onClick={() => toggleRevealAyah(idx)}
                          className="px-3 py-1 bg-violet-600 text-white text-xs rounded-lg hover:bg-violet-700 transition-all flex items-center gap-1"
                        >
                          <Eye className="w-3 h-3" />
                          {lang === 'hr' ? 'Prikaži' : 'Reveal'}
                        </button>
                      )}
                      {ayahResult && (
                        <span className={`text-lg ${
                          ayahResult.result === 'correct' ? 'text-emerald-600' :
                          ayahResult.result === 'partial' ? 'text-amber-600' :
                          'text-red-600'
                        }`}>
                          {ayahResult.result === 'correct' ? '✓' : ayahResult.result === 'partial' ? '~' : '✗'}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="p-4">
                    {/* Arabic Text */}
                    {testMode && testType === 'arabic' && !isRevealed && !ayahResult ? (
                      <div className="mb-3 bg-stone-100 rounded-lg p-4 text-center">
                        <EyeOff className="w-8 h-8 text-stone-400 mx-auto mb-2" />
                        <p className="text-sm text-stone-500">{lang === 'hr' ? 'Pokušaj se sjetiti...' : 'Try to remember...'}</p>
                      </div>
                    ) : (
                      <p className="font-arabic text-right leading-[2.2] mb-3" dir="rtl" style={{ fontSize: '24px', color: '#1c1917' }}>
                        {ayah.text}
                      </p>
                    )}
                    
                    {/* Translation */}
                    {ayah.translation && (
                      testMode && testType === 'translation' && !isRevealed && !ayahResult ? (
                        <div className="bg-stone-100 rounded-lg p-4 text-center">
                          <EyeOff className="w-6 h-6 text-stone-400 mx-auto mb-2" />
                          <p className="text-xs text-stone-500">{lang === 'hr' ? 'Prijevod sakriven' : 'Translation hidden'}</p>
                        </div>
                      ) : (
                        <div className="bg-stone-50 rounded-lg p-3">
                          <p className="text-sm text-stone-600 leading-relaxed">{ayah.translation}</p>
                        </div>
                      )
                    )}
                    
                    {/* Test Mode Assessment Buttons */}
                    {testMode && isRevealed && !ayahResult && (
                      <div className="mt-3 pt-3 border-t border-stone-200">
                        <p className="text-xs text-stone-600 mb-2 text-center">
                          {lang === 'hr' ? 'Kako dobro si znao/la?' : 'How well did you know it?'}
                        </p>
                        <div className="flex gap-2">
                          <button
                            onClick={() => saveTestResult(selectedSurah, ayahNum, 'correct')}
                            className="flex-1 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-all flex items-center justify-center gap-1 text-sm"
                          >
                            <CheckCircle className="w-4 h-4" />
                            {lang === 'hr' ? 'Tačno' : 'Correct'}
                          </button>
                          <button
                            onClick={() => saveTestResult(selectedSurah, ayahNum, 'partial')}
                            className="flex-1 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-all flex items-center justify-center gap-1 text-sm"
                          >
                            <MinusCircle className="w-4 h-4" />
                            {lang === 'hr' ? 'Djelimično' : 'Partial'}
                          </button>
                          <button
                            onClick={() => saveTestResult(selectedSurah, ayahNum, 'incorrect')}
                            className="flex-1 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all flex items-center justify-center gap-1 text-sm"
                          >
                            <XCircle className="w-4 h-4" />
                            {lang === 'hr' ? 'Netačno' : 'Incorrect'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Progress */}
            <div className="bg-stone-50 border border-stone-200 rounded-xl p-4 text-center">
              <div className="text-sm text-[#57534E]">
                {lang === 'hr' ? 'Naučeno u ovom opsegu:' : 'Memorized in this range:'} <span className="font-bold text-emerald-600">{memorizedInRange}/{ayahs.length}</span>
              </div>
              <div className="w-full bg-stone-200 rounded-full h-2 mt-2">
                <div
                  className="bg-emerald-500 h-2 rounded-full transition-all"
                  style={{ width: `${ayahs.length > 0 ? (memorizedInRange / ayahs.length) * 100 : 0}%` }}
                />
              </div>
            </div>
          </div>
        )}
      </div>


      {/* Session Summary Modal */}
      {showSessionSummary && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={() => setShowSessionSummary(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <Check className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-stone-800 mb-1">{lang === 'hr' ? 'Sesija Završena!' : 'Session Complete!'}</h3>
              <p className="text-sm text-stone-500">{lang === 'hr' ? 'Odličan rad!' : 'Great work!'}</p>
            </div>
            
            <div className="space-y-3 mb-6">
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-xl">
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-blue-500" />
                  <span className="text-sm font-medium text-stone-700">{lang === 'hr' ? 'Vrijeme' : 'Duration'}</span>
                </div>
                <span className="text-lg font-bold text-blue-700">{formatDuration(sessionDuration)}</span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-violet-50 rounded-xl">
                <div className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-violet-500" />
                  <span className="text-sm font-medium text-stone-700">{lang === 'hr' ? 'Ajeta' : 'Ayahs'}</span>
                </div>
                <span className="text-lg font-bold text-violet-700">{ayahs.length}</span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-xl">
                <div className="flex items-center gap-2">
                  <Repeat className="w-5 h-5 text-emerald-500" />
                  <span className="text-sm font-medium text-stone-700">{lang === 'hr' ? 'Ukupno ponavljanja' : 'Total Repeats'}</span>
                </div>
                <span className="text-lg font-bold text-emerald-700">{totalRepeatsInSession}</span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-orange-50 rounded-xl">
                <div className="flex items-center gap-2">
                  <Flame className="w-5 h-5 text-orange-500" />
                  <span className="text-sm font-medium text-stone-700">{lang === 'hr' ? 'Streak' : 'Streak'}</span>
                </div>
                <span className="text-lg font-bold text-orange-700">{streakData.current} {lang === 'hr' ? 'dana' : 'days'}</span>
              </div>
            </div>
            
            <button
              onClick={() => setShowSessionSummary(false)}
              className="w-full py-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl font-medium hover:from-violet-700 hover:to-purple-700 transition-all"
            >
              {lang === 'hr' ? 'Zatvori' : 'Close'}
            </button>
          </div>
        </div>
      )}
      
      {/* Progress Modal */}
      {showProgressModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm overflow-y-auto" onClick={() => setShowProgressModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-stone-200 p-4 flex justify-between items-center z-10">
              <h3 className="text-xl font-bold text-stone-800 flex items-center gap-2">
                <TrendingUp className="w-6 h-6 text-violet-600" />
                {lang === 'hr' ? 'Tvoj Napredak' : 'Your Progress'}
              </h3>
              <button onClick={() => setShowProgressModal(false)} className="p-2 hover:bg-stone-100 rounded-lg">
                <X className="w-5 h-5 text-stone-400" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Weekly Activity Chart */}
              <div className="bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-200 rounded-xl p-5">
                <h4 className="text-lg font-semibold text-stone-800 mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-violet-600" />
                  {lang === 'hr' ? 'Sedmična Aktivnost' : 'Weekly Activity'}
                </h4>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={weeklyActivity}>
                    <XAxis dataKey="day" stroke="#78716C" style={{ fontSize: '12px' }} />
                    <YAxis stroke="#78716C" style={{ fontSize: '12px' }} />
                    <Tooltip 
                      contentStyle={{ background: '#fff', border: '1px solid #e7e5e4', borderRadius: '8px' }}
                      formatter={(value) => [`${value} min`, lang === 'hr' ? 'Vrijeme' : 'Time']}
                    />
                    <Bar dataKey="minutes" radius={[8, 8, 0, 0]}>
                      {weeklyActivity.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.minutes > 0 ? '#8b5cf6' : '#e7e5e4'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              
              {/* Surah Progress */}
              <div>
                <h4 className="text-lg font-semibold text-stone-800 mb-3 flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-emerald-600" />
                  {lang === 'hr' ? 'Napredak po Suri' : 'Surah Progress'}
                </h4>
                <div className="grid gap-2 max-h-96 overflow-y-auto">
                  {surahProgressData.filter(s => s.memorized > 0).map(surah => (
                    <div key={surah.number} className="bg-white border border-stone-200 rounded-lg p-3 hover:border-emerald-300 transition-all">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-stone-500">{surah.number}</span>
                          <span className="font-medium text-stone-800 text-sm">{surah.name}</span>
                          {surah.completed && <Award className="w-4 h-4 text-emerald-500" />}
                        </div>
                        <span className="text-sm font-bold text-emerald-600">{surah.percent}%</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-stone-100 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all ${surah.completed ? 'bg-emerald-500' : 'bg-violet-500'}`}
                            style={{ width: `${surah.percent}%` }}
                          />
                        </div>
                        <span className="text-xs text-stone-500">{surah.memorized}/{surah.total}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Juz Progress */}
              <div>
                <h4 className="text-lg font-semibold text-stone-800 mb-3 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  {lang === 'hr' ? 'Napredak po Džuzu' : 'Juz Progress'}
                </h4>
                <div className="grid grid-cols-5 sm:grid-cols-10 gap-2">
                  {juzProgressData.map(juz => {
                    const completed = juz.memorized >= juz.total * 0.9;
                    const started = juz.memorized > 0;
                    return (
                      <div
                        key={juz.number}
                        className={`aspect-square rounded-lg flex items-center justify-center text-sm font-bold transition-all ${
                          completed ? 'bg-emerald-500 text-white' :
                          started ? 'bg-violet-500 text-white' :
                          'bg-stone-100 text-stone-400'
                        }`}
                        title={`Juz ${juz.number}: ${juz.memorized}/${juz.total} ayahs`}
                      >
                        {juz.number}
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center gap-4 mt-3 text-xs text-stone-600">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded bg-emerald-500" />
                    <span>{lang === 'hr' ? 'Završeno' : 'Completed'}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded bg-violet-500" />
                    <span>{lang === 'hr' ? 'U toku' : 'In Progress'}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded bg-stone-100" />
                    <span>{lang === 'hr' ? 'Nije započeto' : 'Not Started'}</span>
                  </div>
                </div>
              </div>
              

              {/* Test Results / Problematic Ayahs */}
              {Object.keys(ayahPerformance).length > 0 && (
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-5">
                  <h4 className="text-lg font-semibold text-stone-800 mb-3 flex items-center gap-2">
                    <Target className="w-5 h-5 text-blue-600" />
                    {lang === 'hr' ? 'Test Rezultati' : 'Test Results'}
                  </h4>
                  
                  {/* Problematic Ayahs */}
                  <div className="mb-4">
                    <p className="text-sm font-medium text-stone-700 mb-2">
                      {lang === 'hr' ? 'Problematični Ajeti (< 70%):' : 'Problematic Ayahs (< 70%):'}
                    </p>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {Object.entries(ayahPerformance)
                        .filter(([_, perf]) => perf.successRate < 70 && perf.total >= 2)
                        .sort((a, b) => a[1].successRate - b[1].successRate)
                        .map(([key, perf]) => {
                          const [surahNum, ayahNum] = key.split(':').map(Number);
                          const surahInfo = surahs.find(s => s.number === surahNum);
                          return (
                            <div key={key} className="bg-white rounded-lg p-3 border border-red-200">
                              <div className="flex items-center justify-between">
                                <div>
                                  <span className="text-sm font-medium text-stone-800">
                                    {surahInfo?.englishName} - {lang === 'hr' ? 'Ajet' : 'Ayah'} {ayahNum}
                                  </span>
                                  <div className="flex gap-2 mt-1 text-xs text-stone-600">
                                    <span>✓ {perf.correct}</span>
                                    <span>~ {perf.partial}</span>
                                    <span>✗ {perf.incorrect}</span>
                                  </div>
                                </div>
                                <div className={`text-lg font-bold ${
                                  perf.successRate >= 70 ? 'text-emerald-600' :
                                  perf.successRate >= 40 ? 'text-amber-600' :
                                  'text-red-600'
                                }`}>
                                  {perf.successRate}%
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      {Object.entries(ayahPerformance).filter(([_, perf]) => perf.successRate < 70 && perf.total >= 2).length === 0 && (
                        <p className="text-sm text-stone-500 text-center py-2">
                          {lang === 'hr' ? '🎉 Nema problematičnih ajeta!' : '🎉 No problematic ayahs!'}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Overall Test Stats */}
                  <div className="grid grid-cols-3 gap-2">
                    <div className="bg-white rounded-lg p-3 text-center border border-blue-200">
                      <div className="text-xl font-bold text-blue-600">
                        {Object.keys(ayahPerformance).length}
                      </div>
                      <div className="text-xs text-stone-600">{lang === 'hr' ? 'Testirano ajeta' : 'Ayahs tested'}</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center border border-blue-200">
                      <div className="text-xl font-bold text-emerald-600">
                        {Object.values(ayahPerformance).reduce((sum, p) => sum + p.correct, 0)}
                      </div>
                      <div className="text-xs text-stone-600">{lang === 'hr' ? 'Tačno' : 'Correct'}</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center border border-blue-200">
                      <div className="text-xl font-bold text-amber-600">
                        {Math.round(
                          Object.values(ayahPerformance).reduce((sum, p) => sum + p.successRate, 0) / 
                          Object.keys(ayahPerformance).length
                        )}%
                      </div>
                      <div className="text-xs text-stone-600">{lang === 'hr' ? 'Prosjek' : 'Average'}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Milestones */}
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-5">
                <h4 className="text-lg font-semibold text-stone-800 mb-3 flex items-center gap-2">
                  <Award className="w-5 h-5 text-amber-600" />
                  {lang === 'hr' ? 'Postignuća' : 'Milestones'}
                </h4>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="bg-white rounded-lg p-3 border border-amber-200">
                    <div className="text-2xl font-bold text-amber-600">{surahProgressData.filter(s => s.completed).length}</div>
                    <div className="text-xs text-stone-600">{lang === 'hr' ? 'Sure završene' : 'Surahs completed'}</div>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-amber-200">
                    <div className="text-2xl font-bold text-amber-600">{juzProgressData.filter(j => j.memorized >= j.total * 0.9).length}</div>
                    <div className="text-xs text-stone-600">{lang === 'hr' ? 'Džuzovi završeni' : 'Juz completed'}</div>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-amber-200">
                    <div className="text-2xl font-bold text-amber-600">{streakData.longest}</div>
                    <div className="text-xs text-stone-600">{lang === 'hr' ? 'Najduži streak' : 'Longest streak'}</div>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-amber-200">
                    <div className="text-2xl font-bold text-amber-600">{weeklyActivity.reduce((sum, d) => sum + d.sessions, 0)}</div>
                    <div className="text-xs text-stone-600">{lang === 'hr' ? 'Sesije ove sedmice' : 'Sessions this week'}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Surah Picker Modal */}
      {showSurahPicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm" onClick={() => setShowSurahPicker(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center p-4 border-b border-stone-200">
              <h3 className="text-lg font-semibold">{lang === 'hr' ? 'Odaberi Suru' : 'Select Surah'}</h3>
              <button onClick={() => setShowSurahPicker(false)} className="p-2 hover:bg-stone-100 rounded-lg"><X className="w-5 h-5 text-stone-400" /></button>
            </div>
            <div className="p-3 border-b border-stone-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <input
                  type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                  placeholder={lang === 'hr' ? 'Pretrazi...' : 'Search...'}
                  className="w-full pl-10 pr-4 py-2 bg-stone-50 border border-stone-200 rounded-lg outline-none focus:ring-2 focus:ring-violet-500/20"
                  autoFocus
                />
              </div>
            </div>
            <div className="overflow-y-auto flex-1 p-2">
              {filteredSurahs.map(s => (
                <button
                  key={s.number}
                  onClick={() => { setSelectedSurah(s.number); setStartAyah(1); setStartAyahInput('1'); setEndAyah(Math.min(5, s.numberOfAyahs)); setEndAyahInput(String(Math.min(5, s.numberOfAyahs))); setShowSurahPicker(false); setAyahs([]); }}
                  className={`w-full text-left px-4 py-3 rounded-lg transition-all ${
                    selectedSurah === s.number ? 'bg-violet-600 text-white' : 'hover:bg-stone-50 text-stone-800'
                  }`}
                >
                  <div className="font-medium">{s.number}. {s.englishName}</div>
                  <div className={`text-sm ${selectedSurah === s.number ? 'text-white/70' : 'text-stone-500'}`}>{s.name} - {s.numberOfAyahs} {lang === 'hr' ? 'ajeta' : 'ayahs'}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
